import { post, get } from "@/services/axiosUitils";
import {
  setFitbitAccessToken,
  setFitbitRefreshToken,
  getFitbitRefreshToken,
  getFitbitAccessToken,
  clearFitbitAccessToken,
} from "../utils/fitbitStorage";

// Define a global lock on the window object to prevent duplicate exchanges across 
// multiple service instances or component remounts (especially in React 18 StrictMode)
declare global {
  interface Window {
    __FITBIT_EXCHANGE_LOCKS__?: Map<string, Promise<any>>;
  }
}

class FitbitAuthService {
  private instanceId = Math.random().toString(36).substring(7);

  private getGlobalLocks(): Map<string, Promise<any>> {
    if (!window.__FITBIT_EXCHANGE_LOCKS__) {
      window.__FITBIT_EXCHANGE_LOCKS__ = new Map<string, Promise<any>>();
    }
    return window.__FITBIT_EXCHANGE_LOCKS__;
  }

  /**
   * Exchange authorization code for access token
   * @param code - Authorization code from Fitbit OAuth
   * @param state - State parameter for security verification
   */
  async exchangeCode(code: string, state: string) {
    const locks = this.getGlobalLocks();

    if (locks.has(code)) {
      return locks.get(code);
    }

    const exchangePromise = (async () => {
      const apiUrl = `/fitbit_scan/callback/?code=${code}&state=${state}`;

      const response = await get(apiUrl);

      // Extract token from nested fitbit_token object with multiple fallback attempts
      let tokenData = (response as any)?.fitbit_token;
      if (!tokenData && response) {
        tokenData = response as any;
      }
      if (!tokenData?.access_token && (response as any)?.data?.fitbit_token) {
        tokenData = (response as any).data.fitbit_token;
      }

      // Store Fitbit access token and refresh token
      if (tokenData?.access_token) {
        const expiresIn = tokenData?.expires_in ?? 28800;
        setFitbitAccessToken(tokenData.access_token, expiresIn);
      }

      if (tokenData?.refresh_token) {
        setFitbitRefreshToken(tokenData.refresh_token);
      }

      return response;
    })();

    locks.set(code, exchangePromise);
    return exchangePromise;
  }

  /**
   * Refresh Fitbit access token using refresh token
   */
  async refreshToken() {
    try {
      const refreshToken = getFitbitRefreshToken();

      if (!refreshToken) {
        throw new Error("No refresh token available. Please login again.");
      }

      const response = await post("/fitbit_scan/refresh/", {
        refresh_token: refreshToken,
      });

      if ((response as any).data?.access_token) {
        const expiresIn = (response as any).data?.expires_in ?? 28800;
        setFitbitAccessToken((response as any).data.access_token, expiresIn);
      }

      return (response as any).data;
    } catch (error: any) {
      // Clear tokens on refresh failure
      clearFitbitAccessToken();
      console.error("Fitbit token refresh failed:", {
        status: error.response?.status,
        message: error.response?.data?.message,
      });
      throw error;
    }
  }

  /**
   * Get Fitbit login URL from backend
   */
  async getLoginUrl() {
    try {
      const response = await get("/fitbit_scan/login/");
      return response;
    } catch (error: any) {
      if (error.response?.status === 401) {
        console.error("Unauthorized access to Fitbit login URL");
        throw new Error("Please log in again to connect Fitbit");
      }
      throw error;
    }
  }

  /**
   * Check if user has connected Fitbit
   */
  async checkConnectionStatus() {
    try {
      const response = await post("/api/fitbit_scan/status/");
      return (response as any).data;
    } catch (_error) {
      return { connected: false };
    }
  }

  /**
   * Disconnect Fitbit account
   */
  async disconnect() {
    const response = await post("/api/fitbit_scan/disconnect/");
    clearFitbitAccessToken();
    return (response as any).data;
  }

  /**
   * Ensure valid Fitbit token - auto refresh if expired
   * @returns true if token is valid/refreshed, false if no refresh token available
   */
  async ensureValidToken(): Promise<boolean> {
    try {
      // Check if we have a valid access token
      const accessToken = getFitbitAccessToken();
      if (accessToken) {
        return true;
      }

      // Token expired, try to refresh
      const refreshToken = getFitbitRefreshToken();

      if (!refreshToken) {
        return false;
      }

      await this.refreshToken();
      return true;
    } catch (_error) {
      return false;
    }
  }

  /**
   * Universal Fitbit Login Popup handler
   * @param onSuccess Callback when login is successful
   * @param onError Callback when login fails
   */
  async loginPopup(onSuccess: () => void, onError: (err: string) => void) {
    let popup: Window | null = null;
    let messageHandler: ((event: MessageEvent) => void) | null = null;

    try {
      const loginData: any = await this.getLoginUrl();
      const authUrl =
        loginData?.auth_url ?? loginData?.authorization_url ?? loginData?.url;

      if (!authUrl) {
        onError("Failed to get Fitbit login URL");
        return;
      }

      // Open centered popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;

      popup = window.open(
        authUrl,
        "FitbitLogin",
        `width=${width},height=${height},left=${left},top=${top},status=no,resizable=yes,toolbar=no,menubar=no,scrollbars=yes`
      );

      if (!popup) {
        onError("Popup blocked! Please allow popups for this site.");
        return;
      }

      messageHandler = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data?.type === "FITBIT_CALLBACK") {
          const { code, state } = event.data;
          try {
            await this.exchangeCode(code, state);
            onSuccess();
          } catch (error) {
            console.error("Error exchanging code:", error);
            onError("Failed to connect Fitbit. Please try again.");
          } finally {
            if (messageHandler) {
              window.removeEventListener("message", messageHandler);
            }
          }
        } else if (event.data?.type === "FITBIT_ERROR") {
          onError(`Fitbit error: ${event.data.error}`);
          if (messageHandler) {
            window.removeEventListener("message", messageHandler);
          }
        }
      };

      window.addEventListener("message", messageHandler);

      // Cleanup on popup close
      const checkPopup = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkPopup);
          if (messageHandler) {
            window.removeEventListener("message", messageHandler);
          }
        }
      }, 1000);
    } catch (error) {
      console.error("Error connecting to Fitbit:", error);
      onError("An error occurred. Please try again.");
      if (popup && !popup.closed) {
        popup.close();
      }
    }
  }

  /**
   * Universal Fitbit Login Redirect handler (Same Tab)
   * @param onError Callback when login URL fetch fails
   */
  async loginRedirect(onError: (err: string) => void) {
    try {
      const loginData: any = await this.getLoginUrl();
      const authUrl =
        loginData?.auth_url ?? loginData?.authorization_url ?? loginData?.url;

      if (!authUrl) {
        onError("Failed to get Fitbit login URL");
        return;
      }

      // Redirect the current window to Fitbit
      window.location.href = authUrl;
    } catch (error) {
      console.error("Error connecting to Fitbit:", error);
      onError("An error occurred. Please try again.");
    }
  }
}

// Export singleton instance
const fitbitAuthService = new FitbitAuthService();
export default fitbitAuthService;
